import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import Navbar from "@/components/Navbar";
import {
  ArrowLeft, Download, Lock, Loader2, Pencil, Sparkles, CheckCircle2,
  ShieldCheck, Save, Plus, Trash2, Share2, Copy, Link as LinkIcon, X, PartyPopper
} from "lucide-react";
import { toast } from "sonner";
import { useAutosave } from "@/lib/useAutosave";
import RevenueChart from "@/components/report/RevenueChart";
import Glossary from "@/components/Glossary";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";

function autoGrow(el) {
  if (!el) return;
  el.style.height = "auto";
  el.style.height = el.scrollHeight + "px";
}

function EditableText({ value, onChange, editable, testid, placeholder, className = "" }) {
  const ref = React.useRef(null);
  React.useEffect(() => { autoGrow(ref.current); }, [value]);
  if (!editable) {
    return (
      <p
        className={`text-slate-700 leading-relaxed whitespace-pre-line ${className}`}
        data-testid={testid}
      >
        {value || <span className="text-slate-400">{placeholder || "—"}</span>}
      </p>
    );
  }
  return (
    <Textarea
      ref={ref}
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      onInput={(e) => autoGrow(e.target)}
      placeholder={placeholder}
      rows={3}
      className={`w-full resize-none border border-transparent hover:border-slate-200 focus:border-slate-300 focus:ring-1 focus:ring-slate-200 bg-white rounded-lg px-3 py-2 text-slate-700 leading-relaxed shadow-none print:border-0 print:p-0 ${className}`}
      data-testid={testid}
    />
  );
}

function Section({ title, children, locked = false, blurOnly = false, actions = null, term = null }) {
  return (
    <section className="relative">
      <div className="flex items-center gap-3 mb-4">
        <h2 className="text-2xl sm:text-3xl font-medium tracking-tight text-slate-900 inline-flex items-center gap-1.5">
          <span>{title}</span>
          {term && <Glossary term={term} />}
        </h2>
        {locked && <Badge variant="secondary" className="bg-slate-100 text-slate-700"><Lock className="h-3 w-3 mr-1" /> Låst</Badge>}
        <div className="ml-auto">{actions}</div>
      </div>
      <div className={locked && blurOnly ? "relative" : ""}>
        <div className={locked && blurOnly ? "blur-sm select-none pointer-events-none" : ""}>
          <div className="rounded-2xl border border-gray-200 bg-slate-50/40 p-6 sm:p-8 print:border print:bg-white print:p-4">
            {children}
          </div>
        </div>
      </div>
    </section>
  );
}

function SwotBlock({ swot, editable, onChange }) {
  if (!swot) return null;
  const groups = [
    { key: "strengths", label: "Styrker", color: "emerald" },
    { key: "weaknesses", label: "Svakheter", color: "rose" },
    { key: "opportunities", label: "Muligheter", color: "amber" },
    { key: "threats", label: "Trusler", color: "slate" },
  ];
  const colorMap = {
    emerald: "bg-emerald-50 border-emerald-100 text-emerald-900",
    rose: "bg-rose-50 border-rose-100 text-rose-900",
    amber: "bg-amber-50 border-amber-100 text-amber-900",
    slate: "bg-slate-50 border-slate-200 text-slate-900",
  };
  const update = (key, items) => onChange({ ...swot, [key]: items });
  return (
    <div className="grid sm:grid-cols-2 gap-4">
      {groups.map((g) => {
        const items = swot[g.key] || [];
        return (
          <div key={g.key} className={`rounded-2xl border p-6 ${colorMap[g.color]}`}>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] mb-3 opacity-70">{g.label}</p>
            <ul className="space-y-2 text-sm leading-relaxed">
              {items.map((s, i) => (
                <li key={i} className="flex items-start gap-2">
                  <span className="mt-2 h-1 w-1 rounded-full bg-current shrink-0 opacity-60" />
                  {editable ? (
                    <div className="flex-1 flex items-start gap-1">
                      <Input
                        value={s}
                        onChange={(e) => {
                          const next = [...items];
                          next[i] = e.target.value;
                          update(g.key, next);
                        }}
                        className="bg-white/60 border-transparent hover:border-current/20 focus:border-current/40 h-8 px-2 text-sm shadow-none"
                        data-testid={`swot-${g.key}-${i}`}
                      />
                      <button
                        type="button"
                        onClick={() => update(g.key, items.filter((_, idx) => idx !== i))}
                        className="text-current/40 hover:text-current p-1 print:hidden"
                        data-testid={`swot-${g.key}-${i}-remove`}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ) : (
                    <span>{s}</span>
                  )}
                </li>
              ))}
            </ul>
            {editable && (
              <button
                type="button"
                onClick={() => update(g.key, [...items, ""])}
                className="mt-3 text-xs opacity-70 hover:opacity-100 inline-flex items-center gap-1 print:hidden"
                data-testid={`swot-${g.key}-add`}
              >
                <Plus className="h-3 w-3" /> Legg til punkt
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
}

function ActionPlan({ steps, editable, onChange }) {
  if (!steps) return null;
  const updateStep = (i, patch) => {
    const next = steps.map((s, idx) => (idx === i ? { ...s, ...patch } : s));
    onChange(next);
  };
  const remove = (i) => onChange(steps.filter((_, idx) => idx !== i));
  const add = () => onChange([...steps, { phase: "Nytt steg", timeline: "", actions: "" }]);
  return (
    <div className="space-y-4">
      {steps.map((s, i) => (
        <div key={i} className="rounded-2xl border border-gray-200 p-6 bg-white">
          <div className="flex items-center gap-3">
            <div className="h-8 w-8 rounded-full bg-slate-900 text-white flex items-center justify-center text-sm font-medium">{i + 1}</div>
            {editable ? (
              <>
                <Input
                  value={s.phase || ""}
                  onChange={(e) => updateStep(i, { phase: e.target.value })}
                  placeholder="Fase"
                  className="flex-1 h-9 border-transparent hover:border-slate-200 focus:border-slate-300 bg-white shadow-none font-medium"
                  data-testid={`action-phase-${i}`}
                />
                <Input
                  value={s.timeline || ""}
                  onChange={(e) => updateStep(i, { timeline: e.target.value })}
                  placeholder="Tidslinje"
                  className="w-40 h-9 border-transparent hover:border-slate-200 focus:border-slate-300 bg-white text-xs text-slate-500 shadow-none"
                  data-testid={`action-timeline-${i}`}
                />
                <button
                  type="button"
                  onClick={() => remove(i)}
                  className="text-slate-300 hover:text-red-500 print:hidden"
                  data-testid={`action-remove-${i}`}
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </>
            ) : (
              <>
                <p className="text-sm font-medium text-slate-900">{s.phase}</p>
                {s.timeline && <span className="text-xs text-slate-500">· {s.timeline}</span>}
              </>
            )}
          </div>
          <div className="mt-3">
            <EditableText
              value={s.actions}
              onChange={(v) => updateStep(i, { actions: v })}
              editable={editable}
              placeholder="Beskriv handlingene i denne fasen..."
              testid={`action-actions-${i}`}
            />
          </div>
        </div>
      ))}
      {editable && (
        <button
          type="button"
          onClick={add}
          className="w-full rounded-2xl border border-dashed border-gray-300 p-4 text-sm text-slate-500 hover:bg-slate-50 inline-flex items-center justify-center gap-2 print:hidden"
          data-testid="action-add"
        >
          <Plus className="h-4 w-4" /> Legg til steg
        </button>
      )}
    </div>
  );
}

function BudgetBlock({ budget, editable, onChange }) {
  if (!budget) return null;
  const set = (k, v) => onChange({ ...budget, [k]: v });
  const fields = [
    { key: "monthly_revenue", label: "Mnd. omsetning" },
    { key: "monthly_costs", label: "Mnd. kostnader" },
    { key: "breakeven", label: "Breakeven" },
  ];
  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-8">
      <div className="grid sm:grid-cols-3 gap-6">
        {fields.map((f) => (
          <div key={f.key}>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">{f.label}</p>
            {editable ? (
              <Input
                value={budget[f.key] || ""}
                onChange={(e) => set(f.key, e.target.value)}
                className="mt-1 text-2xl font-medium tracking-tight border-transparent hover:border-slate-200 focus:border-slate-300 bg-white shadow-none px-0"
                data-testid={`budget-${f.key}`}
              />
            ) : (
              <p className="mt-2 text-2xl font-medium tracking-tight text-slate-900">{budget[f.key]}</p>
            )}
          </div>
        ))}
      </div>
      <div className="my-6 h-px bg-gray-100" />
      <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 mb-2">Forutsetninger</p>
      <EditableText
        value={budget.assumptions}
        onChange={(v) => set("assumptions", v)}
        editable={editable}
        placeholder="Beskriv forutsetningene for budsjettet..."
        testid="budget-assumptions"
      />
      {Array.isArray(budget.revenue_projection) && budget.revenue_projection.length > 0 && (
        <>
          <div className="my-6 h-px bg-gray-100" />
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 mb-3">Omsetning per måned</p>
          <RevenueChart data={budget.revenue_projection} />
        </>
      )}
    </div>
  );
}

function MarketSizeBlock({ market, editable, onChange }) {
  if (!market) return null;
  const items = [
    { key: "tam", label: "TAM", desc: "Totalt marked" },
    { key: "sam", label: "SAM", desc: "Serviceable marked" },
    { key: "som", label: "SOM", desc: "Realistisk andel" },
  ];
  const set = (k, v) => onChange({ ...market, [k]: v });
  return (
    <div className="grid sm:grid-cols-3 gap-4">
      {items.map((i) => (
        <div key={i.key} className="rounded-2xl border border-gray-200 bg-white p-6">
          <div className="flex items-baseline gap-2">
            <span className="text-2xl font-medium tracking-tight text-slate-900">{i.label}</span>
            <span className="text-xs text-slate-500">{i.desc}</span>
          </div>
          <div className="mt-3">
            <EditableText
              value={market[i.key]}
              onChange={(v) => set(i.key, v)}
              editable={editable}
              placeholder={`Estimert ${i.label}...`}
              testid={`market-${i.key}`}
              className="text-sm"
            />
          </div>
        </div>
      ))}
    </div>
  );
}

function KPIsBlock({ kpis, editable, onChange }) {
  if (!Array.isArray(kpis)) return null;
  const upd = (i, patch) => onChange(kpis.map((k, idx) => idx === i ? { ...k, ...patch } : k));
  const remove = (i) => onChange(kpis.filter((_, idx) => idx !== i));
  const add = () => onChange([...kpis, { name: "Ny KPI", target: "", unit: "" }]);
  return (
    <div className="space-y-4">
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {kpis.map((k, i) => (
          <div key={i} className="rounded-2xl border border-gray-200 bg-white p-6 relative">
            {editable && (
              <button
                type="button"
                onClick={() => remove(i)}
                className="absolute top-3 right-3 text-slate-300 hover:text-red-500 print:hidden"
                data-testid={`kpi-remove-${i}`}
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            )}
            {editable ? (
              <>
                <Input
                  value={k.name || ""}
                  onChange={(e) => upd(i, { name: e.target.value })}
                  placeholder="Navn"
                  className="border-transparent hover:border-slate-200 focus:border-slate-300 bg-white shadow-none text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 px-0"
                  data-testid={`kpi-name-${i}`}
                />
                <Input
                  value={k.target || ""}
                  onChange={(e) => upd(i, { target: e.target.value })}
                  placeholder="Mål"
                  className="mt-1 border-transparent hover:border-slate-200 focus:border-slate-300 bg-white shadow-none text-2xl font-medium tracking-tight text-slate-900 px-0"
                  data-testid={`kpi-target-${i}`}
                />
                <Input
                  value={k.unit || ""}
                  onChange={(e) => upd(i, { unit: e.target.value })}
                  placeholder="Enhet"
                  className="mt-1 border-transparent hover:border-slate-200 focus:border-slate-300 bg-white shadow-none text-xs text-slate-500 px-0"
                  data-testid={`kpi-unit-${i}`}
                />
              </>
            ) : (
              <>
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">{k.name}</p>
                <p className="mt-2 text-2xl font-medium tracking-tight text-slate-900">{k.target}</p>
                {k.unit && <p className="mt-1 text-xs text-slate-500">{k.unit}</p>}
              </>
            )}
          </div>
        ))}
      </div>
      {editable && (
        <button
          type="button"
          onClick={add}
          className="w-full rounded-2xl border border-dashed border-gray-300 p-4 text-sm text-slate-500 hover:bg-slate-50 inline-flex items-center justify-center gap-2 print:hidden"
          data-testid="kpi-add"
        >
          <Plus className="h-4 w-4" /> Legg til KPI
        </button>
      )}
    </div>
  );
}

function RisksBlock({ risks, editable, onChange }) {
  if (!Array.isArray(risks)) return null;
  const upd = (i, patch) => onChange(risks.map((r, idx) => idx === i ? { ...r, ...patch } : r));
  const remove = (i) => onChange(risks.filter((_, idx) => idx !== i));
  const add = () => onChange([...risks, { risk: "Ny risiko", mitigation: "" }]);
  return (
    <div className="space-y-3">
      {risks.map((r, i) => (
        <div key={i} className="rounded-2xl border border-gray-200 bg-white p-6">
          <div className="flex items-start gap-3">
            <div className="h-8 w-8 rounded-full bg-rose-100 text-rose-700 flex items-center justify-center text-sm font-medium shrink-0">{i + 1}</div>
            <div className="flex-1">
              <div className="flex items-center justify-between gap-2">
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-rose-700">Risiko</p>
                {editable && (
                  <button
                    type="button"
                    onClick={() => remove(i)}
                    className="text-slate-300 hover:text-red-500 print:hidden"
                    data-testid={`risk-remove-${i}`}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
              <EditableText
                value={r.risk}
                onChange={(v) => upd(i, { risk: v })}
                editable={editable}
                placeholder="Beskriv risikoen..."
                testid={`risk-${i}`}
                className="font-medium text-slate-900"
              />
              <div className="mt-3 pt-3 border-t border-gray-100">
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-emerald-700 mb-1">Tiltak</p>
                <EditableText
                  value={r.mitigation}
                  onChange={(v) => upd(i, { mitigation: v })}
                  editable={editable}
                  placeholder="Konkret tiltak..."
                  testid={`risk-mitigation-${i}`}
                />
              </div>
            </div>
          </div>
        </div>
      ))}
      {editable && (
        <button
          type="button"
          onClick={add}
          className="w-full rounded-2xl border border-dashed border-gray-300 p-4 text-sm text-slate-500 hover:bg-slate-50 inline-flex items-center justify-center gap-2 print:hidden"
          data-testid="risk-add"
        >
          <Plus className="h-4 w-4" /> Legg til risiko
        </button>
      )}
    </div>
  );
}

function NextStepsBlock({ steps, editable, onChange }) {
  if (!Array.isArray(steps)) return null;
  const upd = (i, v) => onChange(steps.map((s, idx) => idx === i ? v : s));
  const remove = (i) => onChange(steps.filter((_, idx) => idx !== i));
  const add = () => onChange([...steps, ""]);
  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-6 sm:p-8">
      <ul className="space-y-3">
        {steps.map((s, i) => (
          <li key={i} className="flex items-start gap-3 text-slate-800">
            <input type="checkbox" className="mt-1.5 h-4 w-4 rounded border-gray-300" data-testid={`step-check-${i}`} />
            {editable ? (
              <div className="flex-1 flex items-center gap-2">
                <Input
                  value={s}
                  onChange={(e) => upd(i, e.target.value)}
                  className="flex-1 border-transparent hover:border-slate-200 focus:border-slate-300 bg-white shadow-none h-9"
                  data-testid={`step-${i}`}
                />
                <button type="button" onClick={() => remove(i)} className="text-slate-300 hover:text-red-500 print:hidden" data-testid={`step-remove-${i}`}>
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            ) : (
              <span className="leading-relaxed">{s}</span>
            )}
          </li>
        ))}
      </ul>
      {editable && (
        <button
          type="button"
          onClick={add}
          className="mt-4 text-xs text-slate-500 hover:text-slate-900 inline-flex items-center gap-1 print:hidden"
          data-testid="step-add"
        >
          <Plus className="h-3 w-3" /> Legg til steg
        </button>
      )}
    </div>
  );
}

function ConclusionBlock({ text, editable, onChange }) {
  if (text == null && !editable) return null;
  return (
    <div className="rounded-2xl bg-slate-900 text-white p-8">
      <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-300 mb-4">Konklusjon</p>
      {editable ? (
        <Textarea
          value={text || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Skriv en konklusjon..."
          rows={5}
          className="w-full resize-none border-transparent bg-slate-800/50 text-white placeholder:text-slate-400 shadow-none text-lg leading-relaxed"
          data-testid="conclusion-text"
        />
      ) : (
        <p className="text-lg leading-relaxed text-slate-100 whitespace-pre-line">{text}</p>
      )}
    </div>
  );
}

function SaveStatus({ status }) {
  if (status === "saving") return <span className="text-xs text-slate-400 inline-flex items-center gap-1"><Loader2 className="h-3 w-3 animate-spin" /> Lagrer...</span>;
  if (status === "saved") return <span className="text-xs text-emerald-600 inline-flex items-center gap-1"><CheckCircle2 className="h-3 w-3" /> Lagret</span>;
  if (status === "error") return <span className="text-xs text-red-600">Lagring feilet</span>;
  return null;
}

export default function PlanView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [plan, setPlan] = useState(null);
  const [checkout, setCheckout] = useState(false);
  const [consent, setConsent] = useState(false);
  const [regen, setRegen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [draft, setDraft] = useState(null);
  const [shareOpen, setShareOpen] = useState(false);
  const [shareBusy, setShareBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/plans/${id}`);
      setPlan(data);
      setDraft(data.sections || null);
    } catch {
      toast.error("Plan ikke funnet");
      navigate("/dashboard");
    }
  };

  useEffect(() => { load(); }, [id]);

  const saveSections = useCallback(async (sections) => {
    if (!sections) return;
    const { data } = await api.patch(`/plans/${id}/sections`, { sections });
    setPlan(data);
  }, [id]);

  const { status: saveStatus, save: flushSave } = useAutosave(draft, saveSections, 1000, editMode);

  const startCheckout = async () => {
    if (!consent) {
      toast.error("Du må samtykke til umiddelbar levering før du kan betale.");
      return;
    }
    setCheckout(true);
    try {
      const { data } = await api.post("/checkout/session", {
        plan_id: id,
        origin_url: window.location.origin,
        consent_immediate_delivery: consent,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Kunne ikke starte betalingen");
      setCheckout(false);
    }
  };

  const regenerate = async () => {
    setRegen(true);
    try {
      await api.post(`/plans/${id}/generate`);
      const start = Date.now();
      const poll = async () => {
        const { data } = await api.get(`/plans/${id}`);
        if (data.status === "generated") {
          setPlan(data);
          setDraft(data.sections || null);
          toast.success("Planen er regenerert");
          setRegen(false);
          return;
        }
        if (data.status === "error") {
          throw new Error(data.generation_error || "Regenerering feilet");
        }
        if (Date.now() - start > 180000) {
          throw new Error("Regenerering tok for lang tid");
        }
        setTimeout(poll, 3000);
      };
      await poll();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message || "Kunne ikke regenerere");
      setRegen(false);
    }
  };

  const downloadPdf = async () => {
    if (editMode) {
      await flushSave();
      setEditMode(false);
    }
    try {
      const res = await api.get(`/plans/${id}/pdf`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const cd = res.headers?.["content-disposition"] || "";
      const m = cd.match(/filename="([^"]+)"/);
      a.download = m ? m[1] : "planiq.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e.response?.status === 402 ? "Planen er ikke låst opp enda" : "Nedlasting feilet");
    }
  };

  const toggleEdit = async () => {
    if (editMode) {
      await flushSave();
      toast.success("Endringene er lagret");
    }
    setEditMode(!editMode);
  };

  const duplicate = async () => {
    try {
      const { data } = await api.post(`/plans/${id}/duplicate`);
      toast.success("Plan duplisert");
      navigate(`/plan/${data.plan_id}/wizard`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Kunne ikke duplisere");
    }
  };

  const createShareLink = async () => {
    setShareBusy(true);
    try {
      const { data } = await api.post(`/plans/${id}/share`);
      setPlan((p) => ({ ...p, share_token: data.share_token }));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Kunne ikke lage delelenke");
    } finally {
      setShareBusy(false);
    }
  };

  const revokeShareLink = async () => {
    setShareBusy(true);
    try {
      await api.delete(`/plans/${id}/share`);
      setPlan((p) => ({ ...p, share_token: null }));
      toast.success("Delelenken er trukket tilbake");
    } catch {
      toast.error("Kunne ikke trekke tilbake");
    } finally {
      setShareBusy(false);
    }
  };

  const copyShareUrl = () => {
    if (!plan?.share_token) return;
    const url = `${window.location.origin}/delt/${plan.share_token}`;
    navigator.clipboard.writeText(url).then(
      () => toast.success("Lenke kopiert!"),
      () => toast.error("Kunne ikke kopiere")
    );
  };

  if (!plan) {
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;
  }

  const sections = (editMode ? draft : plan.sections) || {};
  const unlocked = !!plan.unlocked;
  const title = plan.inputs?.business_name || plan.title || "Forretningsplan";

  const updateField = (key) => (value) => {
    setDraft((d) => ({ ...(d || {}), [key]: value }));
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <div className="no-print"><Navbar /></div>

      <main className="max-w-4xl mx-auto px-6 lg:px-8 py-10 printable">
        <div className="no-print flex items-center justify-between mb-8 gap-3 flex-wrap">
          <Link to="/dashboard" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-900" data-testid="planview-back">
            <ArrowLeft className="h-4 w-4" /> Tilbake
          </Link>
          <div className="flex items-center gap-2 flex-wrap">
            {editMode && <SaveStatus status={saveStatus} />}
            {unlocked && (
              <Button
                variant={editMode ? "default" : "outline"}
                onClick={toggleEdit}
                className={editMode ? "rounded-full bg-slate-900 hover:bg-slate-800" : "rounded-full"}
                data-testid="planview-edit-toggle"
              >
                {editMode ? <><Save className="h-4 w-4 mr-2" /> Ferdig</> : <><Pencil className="h-4 w-4 mr-2" /> Rediger</>}
              </Button>
            )}
            {unlocked && (
              <Button variant="outline" onClick={() => setShareOpen(true)} className="rounded-full" data-testid="planview-share">
                <Share2 className="h-4 w-4 mr-2" /> Del
              </Button>
            )}
            <Button variant="outline" onClick={duplicate} className="rounded-full" data-testid="planview-duplicate">
              <Copy className="h-4 w-4 mr-2" /> Dupliser
            </Button>
            <Button variant="outline" onClick={() => navigate(`/plan/${id}/wizard`)} className="rounded-full" data-testid="planview-wizard">
              <Pencil className="h-4 w-4 mr-2" /> Endre inndata
            </Button>
            <Button variant="outline" onClick={regenerate} disabled={regen} className="rounded-full" data-testid="planview-regen">
              {regen ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Sparkles className="h-4 w-4 mr-2" /> Regenerer</>}
            </Button>
            {unlocked && (
              <Button asChild variant="outline" className="rounded-full" data-testid="planview-onboarding">
                <Link to={`/plan/${id}/gratulerer`}>
                  <PartyPopper className="h-4 w-4 mr-2" /> Neste steg
                </Link>
              </Button>
            )}
            {unlocked && (
              <Button onClick={downloadPdf} className="rounded-full bg-slate-900 hover:bg-slate-800" data-testid="planview-pdf">
                <Download className="h-4 w-4 mr-2" /> Last ned PDF
              </Button>
            )}
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-10 sm:p-14 printable">
          {/* Cover */}
          <div className="border-b border-gray-100 pb-10 print-cover">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Forretningsplan</p>
            <h1 className="mt-3 text-4xl sm:text-5xl font-medium tracking-tight text-slate-900" data-testid="planview-title">{title}</h1>
            {plan.inputs?.business_idea && <p className="mt-4 text-lg text-slate-600 leading-relaxed">{plan.inputs.business_idea}</p>}
            <div className="mt-6 flex flex-wrap gap-2 text-sm">
              {plan.inputs?.industry && <Badge variant="secondary" className="rounded-full">{plan.inputs.industry}</Badge>}
              {plan.inputs?.company_form && <Badge variant="secondary" className="rounded-full">{plan.inputs.company_form}</Badge>}
              {plan.inputs?.target_market && <Badge variant="secondary" className="rounded-full">{plan.inputs.target_market}</Badge>}
            </div>
          </div>

          {/* Sections */}
          <div className="mt-12 space-y-14">
            <Section title="Sammendrag">
              <EditableText value={sections.executive_summary} onChange={updateField("executive_summary")} editable={editMode} testid="section-executive" />
            </Section>

            <Section title="Forretningsidé">
              <EditableText value={sections.business_idea} onChange={updateField("business_idea")} editable={editMode} testid="section-idea" />
            </Section>

            <div className={unlocked ? "" : "relative"}>
              <div className={unlocked ? "space-y-14" : "space-y-14"}>
                <Section title="Målgruppe" locked={!unlocked} blurOnly={!unlocked}>
                  <EditableText value={sections.target_audience} onChange={updateField("target_audience")} editable={editMode && unlocked} testid="section-audience" />
                </Section>

                <Section title="Problem & løsning" locked={!unlocked} blurOnly={!unlocked}>
                  <EditableText value={sections.problem_solution} onChange={updateField("problem_solution")} editable={editMode && unlocked} testid="section-problem" />
                </Section>

                <Section title="Inntektsmodell" locked={!unlocked} blurOnly={!unlocked}>
                  <EditableText value={sections.revenue_model} onChange={updateField("revenue_model")} editable={editMode && unlocked} testid="section-revenue" />
                </Section>

                {(sections.market_size || (editMode && unlocked)) && (
                  <Section title="Markedsstørrelse" term="tam" locked={!unlocked} blurOnly={!unlocked}>
                    <MarketSizeBlock market={sections.market_size || { tam: "", sam: "", som: "" }} editable={editMode && unlocked} onChange={updateField("market_size")} />
                  </Section>
                )}

                <Section title="SWOT-analyse" term="swot" locked={!unlocked} blurOnly={!unlocked}>
                  <SwotBlock swot={sections.swot} editable={editMode && unlocked} onChange={updateField("swot")} />
                </Section>

                <Section title="Markedsføringsplan" locked={!unlocked} blurOnly={!unlocked}>
                  <EditableText value={sections.marketing_plan} onChange={updateField("marketing_plan")} editable={editMode && unlocked} testid="section-marketing" />
                </Section>

                <Section title="Handlingsplan" locked={!unlocked} blurOnly={!unlocked}>
                  <ActionPlan steps={sections.action_plan} editable={editMode && unlocked} onChange={updateField("action_plan")} />
                </Section>

                {(sections.kpis || (editMode && unlocked)) && (
                  <Section title="Nøkkeltall (KPI-er)" term="kpi" locked={!unlocked} blurOnly={!unlocked}>
                    <KPIsBlock kpis={sections.kpis || []} editable={editMode && unlocked} onChange={updateField("kpis")} />
                  </Section>
                )}

                <Section title="Budsjett" term="breakeven" locked={!unlocked} blurOnly={!unlocked}>
                  <BudgetBlock budget={sections.budget} editable={editMode && unlocked} onChange={updateField("budget")} />
                </Section>

                {(sections.risks || (editMode && unlocked)) && (
                  <Section title="Risikoanalyse" locked={!unlocked} blurOnly={!unlocked}>
                    <RisksBlock risks={sections.risks || []} editable={editMode && unlocked} onChange={updateField("risks")} />
                  </Section>
                )}

                {(sections.next_steps || (editMode && unlocked)) && (
                  <Section title="Neste steg" locked={!unlocked} blurOnly={!unlocked}>
                    <NextStepsBlock steps={sections.next_steps || []} editable={editMode && unlocked} onChange={updateField("next_steps")} />
                  </Section>
                )}

                {(sections.conclusion || (editMode && unlocked)) && (
                  <Section title="Konklusjon" locked={!unlocked} blurOnly={!unlocked}>
                    <ConclusionBlock text={sections.conclusion} editable={editMode && unlocked} onChange={updateField("conclusion")} />
                  </Section>
                )}
              </div>

              {!unlocked && (
                <Card className="no-print absolute inset-x-0 top-0 mt-2 mx-auto max-w-xl bg-white border border-gray-200 shadow-xl rounded-2xl" data-testid="paywall-card">
                  <CardContent className="p-10 text-center">
                    <div className="mx-auto h-12 w-12 rounded-xl bg-slate-900 text-white flex items-center justify-center mb-4">
                      <Lock className="h-5 w-5" />
                    </div>
                    <h3 className="text-2xl font-medium tracking-tight text-slate-900">Lås opp full forretningsplan</h3>
                    <p className="mt-3 text-slate-600">Få tilgang til alle seksjoner, SWOT, handlingsplan, budsjett og PDF-eksport.</p>
                    <div className="mt-6 flex items-baseline justify-center gap-1">
                      <span className="text-4xl font-medium tracking-tight">299</span>
                      <span className="text-slate-500">kr</span>
                      <span className="text-sm text-slate-400 ml-2">engangsbeløp</span>
                    </div>
                    <label className="mt-6 flex items-start gap-3 text-left rounded-xl bg-slate-50 border border-gray-200 p-4 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={consent}
                        onChange={(e) => setConsent(e.target.checked)}
                        className="mt-1 h-4 w-4 accent-slate-900 shrink-0"
                        data-testid="paywall-consent-checkbox"
                      />
                      <span className="text-xs text-slate-600 leading-relaxed">
                        Jeg samtykker til at leveringen av det digitale innholdet starter umiddelbart, og erkjenner at angreretten går tapt når leveringen starter. Se <Link to="/vilkar" target="_blank" className="underline hover:text-slate-900">vilkårene</Link>.
                      </span>
                    </label>
                    <Button onClick={startCheckout} disabled={checkout || !consent} className="mt-4 w-full h-12 rounded-full bg-slate-900 hover:bg-slate-800 text-white disabled:opacity-50" data-testid="paywall-unlock-btn">
                      {checkout ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Lås opp nå med Stripe</>}
                    </Button>
                    <div className="mt-4 flex items-center justify-center gap-3 text-xs text-slate-500">
                      <span className="inline-flex items-center gap-1"><ShieldCheck className="h-3.5 w-3.5" /> Sikker betaling</span>
                      <span className="inline-flex items-center gap-1"><CheckCircle2 className="h-3.5 w-3.5" /> Ingen abonnement</span>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>

          {/* Footer of doc */}
          <div className="mt-16 pt-8 border-t border-gray-100 text-xs text-slate-400 text-center">
            Generert med IdeSetGo · {new Date().toLocaleDateString("nb-NO", { day: "numeric", month: "long", year: "numeric" })}
          </div>
        </div>
      </main>

      <Dialog open={shareOpen} onOpenChange={setShareOpen}>
        <DialogContent className="sm:max-w-md" data-testid="share-dialog">
          <DialogHeader>
            <DialogTitle>Del med rådgiver eller investor</DialogTitle>
            <DialogDescription>
              Opprett en lesetilgang-lenke som du kan sende til revisor, bank eller investor. Ingen innlogging kreves for mottaker.
            </DialogDescription>
          </DialogHeader>
          {plan?.share_token ? (
            <div className="space-y-4">
              <div className="flex items-center gap-2 rounded-xl border border-gray-200 bg-slate-50 p-3">
                <LinkIcon className="h-4 w-4 text-slate-400 shrink-0" />
                <input
                  readOnly
                  value={`${window.location.origin}/delt/${plan.share_token}`}
                  className="flex-1 bg-transparent text-sm text-slate-700 outline-none"
                  data-testid="share-url"
                />
                <Button size="sm" onClick={copyShareUrl} className="rounded-full h-8" data-testid="share-copy-btn">
                  <Copy className="h-3.5 w-3.5 mr-1" /> Kopier
                </Button>
              </div>
              <div className="flex items-center gap-2 text-xs text-slate-500">
                <ShieldCheck className="h-3.5 w-3.5" /> Kun lesetilgang. Innhold kan ikke endres av mottaker.
              </div>
            </div>
          ) : (
            <div className="text-sm text-slate-600">
              Ingen delelenke aktiv. Opprett en for å dele denne planen.
            </div>
          )}
          <DialogFooter className="gap-2">
            {plan?.share_token ? (
              <Button variant="outline" onClick={revokeShareLink} disabled={shareBusy} className="rounded-full" data-testid="share-revoke-btn">
                <X className="h-4 w-4 mr-2" /> Trekk tilbake
              </Button>
            ) : (
              <Button onClick={createShareLink} disabled={shareBusy} className="rounded-full bg-slate-900 hover:bg-slate-800" data-testid="share-create-btn">
                {shareBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Opprett delelenke"}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
